/**
 * RBAC Role-Permission Matrix — Data
 *
 * Contains the complete role-to-permission mapping constant.
 * Separated from role-matrix.ts to keep the logic module under 300 lines.
 *
 * NOTE: This file exceeds the 300-line standard (CODE_STANDARDS rule 1.3).
 * This is an accepted exception — it is a pure data file containing the
 * 5-role × 92-permission matrix. No logic resides here. Consider code
 * generation if the matrix grows beyond 5 roles.
 *
 * Source of truth: docs/rbac/05-role-permission-matrix.md
 */

import type { PermissionKey } from './permissions';
import type { UserRole, AccessLevel } from './roles';

/**
 * Complete role-permission matrix.
 * Every PermissionKey has an entry for every UserRole.
 *
 * Source of truth: docs/rbac/05-role-permission-matrix.md
 */
export const ROLE_PERMISSION_MATRIX: Record<UserRole, Record<PermissionKey, AccessLevel>> = {
  /*
   * -----------------------------------------------------------------------
   * SUPER ADMIN
   * -----------------------------------------------------------------------
   * Access philosophy: Unrestricted access to every permission in the system.
   * This role is the platform owner / root operator. All 92 permissions are
   * set to 'full' with no approval gates.
   *
   * Restrictions: None. Super admins are the only role that can manage
   * users, assign roles, and mutate system settings.
   *
   * requires_approval: Not used. Super admins bypass all approval workflows.
   * -----------------------------------------------------------------------
   */
  super_admin: {
    // 1. Customers
    'customers:list': 'full',
    'customers:view': 'full',
    'customers:create': 'full',
    'customers:edit': 'full',
    'customers:delete': 'full',
    'customers:export': 'full',
    // 2. Subscriptions
    'subscriptions:list': 'full',
    'subscriptions:view': 'full',
    'subscriptions:create': 'full',
    'subscriptions:edit': 'full',
    'subscriptions:pause': 'full',
    'subscriptions:resume': 'full',
    'subscriptions:cancel': 'full',
    'subscriptions:export': 'full',
    // 3. Invoices
    'invoices:list': 'full',
    'invoices:view': 'full',
    'invoices:create': 'full',
    'invoices:close': 'full',
    'invoices:void': 'full',
    'invoices:write_off': 'full',
    'invoices:export': 'full',
    // 4. Credit Notes
    'credit_notes:list': 'full',
    'credit_notes:view': 'full',
    'credit_notes:create': 'full',
    // 5. Transactions
    'transactions:list': 'full',
    'transactions:view': 'full',
    'transactions:export': 'full',
    // 6. Payment Methods
    'payment_methods:view': 'full',
    'payment_methods:create': 'full',
    'payment_methods:edit': 'full',
    'payment_methods:delete': 'full',
    // 7. Entitlements
    'entitlements:view': 'full',
    'entitlements:create': 'full',
    'entitlements:revoke': 'full',
    // 8. Plans
    'plans:list': 'full',
    'plans:view': 'full',
    'plans:create': 'full',
    'plans:edit': 'full',
    'plans:delete': 'full',
    // 9. Addons
    'addons:list': 'full',
    'addons:view': 'full',
    'addons:create': 'full',
    'addons:edit': 'full',
    'addons:delete': 'full',
    // 10. Coupons
    'coupons:list': 'full',
    'coupons:view': 'full',
    'coupons:create': 'full',
    'coupons:edit': 'full',
    'coupons:delete': 'full',
    // 11. Dunning
    'dunning:view': 'full',
    'dunning:view_config': 'full',
    'dunning:configure': 'full',
    'dunning:retry': 'full',
    'dunning:cancel_attempt': 'full',
    // 12. Collections
    'collections:list': 'full',
    'collections:view': 'full',
    'collections:view_config': 'full',
    'collections:configure': 'full',
    'collections:trigger': 'full',
    'collections:pause': 'full',
    'collections:resume': 'full',
    'collections:export': 'full',
    // 13. Alerts / Notifications
    'alerts:list': 'full',
    'alerts:view': 'full',
    'alerts:manage': 'full',
    'alerts:manage_all': 'full',
    // 14. Developer / API Keys
    'developer:api_keys_view': 'full',
    'developer:api_keys_create': 'full',
    'developer:api_keys_revoke': 'full',
    'developer:api_keys_rotate': 'full',
    'developer:stripe_simulate': 'full',
    // 15. Webhooks
    'webhooks:list': 'full',
    'webhooks:create': 'full',
    'webhooks:edit': 'full',
    'webhooks:delete': 'full',
    // 16. Reports
    'reports:view': 'full',
    'reports:export': 'full',
    'reports:generate': 'full',
    // 17. Settings / System
    'settings:view': 'full',
    'settings:edit': 'full',
    // 18. Audit Logs
    'audit_logs:view': 'full',
    'audit_logs:export': 'full',
    // 19. Users & Roles
    'users:list': 'full',
    'users:view': 'full',
    'users:create': 'full',
    'users:edit': 'full',
    'users:delete': 'full',
    'users:assign_role': 'full',
    'roles:view': 'full',
    'roles:create': 'full',
    'roles:edit': 'full',
    'roles:delete': 'full',
    // 20. Tenants
    'tenants:list': 'full',
  },

  /*
   * -----------------------------------------------------------------------
   * ADMIN
   * -----------------------------------------------------------------------
   * Access philosophy: Full day-to-day operational access across customers,
   * subscriptions, invoices, plans, addons, coupons, dunning, collections,
   * alerts, and reports. Admins can perform nearly all business operations
   * without escalation.
   *
   * Restrictions:
   *  - Cannot create, edit, or delete users or roles (super_admin only).
   *  - Cannot create, revoke, or rotate API keys (developer only).
   *  - Cannot create, edit, or delete webhooks (developer only).
   *  - Cannot edit system settings (super_admin only).
   *
   * requires_approval — used for irreversible financial and catalog actions:
   *  - invoices:void / invoices:write_off — Voiding or writing off an
   *    invoice has direct revenue impact and cannot be undone.
   *  - credit_notes:create — Credit notes reduce receivables; approval
   *    prevents unauthorized adjustments.
   *  - plans:delete / addons:delete / coupons:delete — Deleting catalog
   *    items can break active subscriptions; approval ensures coordination.
   *  - audit_logs:export — Bulk export of audit data is sensitive for
   *    compliance; approval provides an additional access control layer.
   * -----------------------------------------------------------------------
   */
  admin: {
    'customers:list': 'full',
    'customers:view': 'full',
    'customers:create': 'full',
    'customers:edit': 'full',
    'customers:delete': 'full',
    'customers:export': 'full',
    'subscriptions:list': 'full',
    'subscriptions:view': 'full',
    'subscriptions:create': 'full',
    'subscriptions:edit': 'full',
    'subscriptions:pause': 'full',
    'subscriptions:resume': 'full',
    'subscriptions:cancel': 'full',
    'subscriptions:export': 'full',
    'invoices:list': 'full',
    'invoices:view': 'full',
    'invoices:create': 'full',
    'invoices:close': 'full',
    'invoices:void': 'requires_approval',
    'invoices:write_off': 'requires_approval',
    'invoices:export': 'full',
    'credit_notes:list': 'full',
    'credit_notes:view': 'full',
    'credit_notes:create': 'requires_approval',
    'transactions:list': 'full',
    'transactions:view': 'full',
    'transactions:export': 'full',
    'payment_methods:view': 'full',
    'payment_methods:create': 'full',
    'payment_methods:edit': 'full',
    'payment_methods:delete': 'full',
    'entitlements:view': 'full',
    'entitlements:create': 'full',
    'entitlements:revoke': 'full',
    'plans:list': 'full',
    'plans:view': 'full',
    'plans:create': 'full',
    'plans:edit': 'full',
    'plans:delete': 'requires_approval',
    'addons:list': 'full',
    'addons:view': 'full',
    'addons:create': 'full',
    'addons:edit': 'full',
    'addons:delete': 'requires_approval',
    'coupons:list': 'full',
    'coupons:view': 'full',
    'coupons:create': 'full',
    'coupons:edit': 'full',
    'coupons:delete': 'requires_approval',
    'dunning:view': 'full',
    'dunning:view_config': 'full',
    'dunning:configure': 'full',
    'dunning:retry': 'full',
    'dunning:cancel_attempt': 'full',
    'collections:list': 'full',
    'collections:view': 'full',
    'collections:view_config': 'full',
    'collections:configure': 'full',
    'collections:trigger': 'full',
    'collections:pause': 'full',
    'collections:resume': 'full',
    'collections:export': 'full',
    'alerts:list': 'full',
    'alerts:view': 'full',
    'alerts:manage': 'full',
    'alerts:manage_all': 'full',
    'developer:api_keys_view': 'full',
    'developer:api_keys_create': 'not_allowed',
    'developer:api_keys_revoke': 'not_allowed',
    'developer:api_keys_rotate': 'not_allowed',
    'developer:stripe_simulate': 'full',
    'webhooks:list': 'full',
    'webhooks:create': 'not_allowed',
    'webhooks:edit': 'not_allowed',
    'webhooks:delete': 'not_allowed',
    'reports:view': 'full',
    'reports:export': 'full',
    'reports:generate': 'full',
    'settings:view': 'full',
    'settings:edit': 'not_allowed',
    'audit_logs:view': 'full',
    'audit_logs:export': 'requires_approval',
    'users:list': 'full',
    'users:view': 'full',
    'users:create': 'not_allowed',
    'users:edit': 'not_allowed',
    'users:delete': 'not_allowed',
    'users:assign_role': 'not_allowed',
    'roles:view': 'full',
    'roles:create': 'not_allowed',
    'roles:edit': 'not_allowed',
    'roles:delete': 'not_allowed',
    // 20. Tenants
    'tenants:list': 'full',
  },

  /*
   * -----------------------------------------------------------------------
   * NORMAL USER (Support Agent)
   * -----------------------------------------------------------------------
   * Access philosophy: Standard support agent handling day-to-day customer
   * interactions. Can view most resources, create customers, resume
   * subscriptions, retry dunning, and manage their own alerts. Edits are
   * scoped to 'own_only' where applicable.
   *
   * Restrictions:
   *  - Cannot create or mutate invoices, credit notes, plans, addons,
   *    coupons, or collections configuration.
   *  - Cannot access developer tools, webhooks, audit logs, or user
   *    management.
   *  - Cannot export data (customers, subscriptions, transactions,
   *    collections, reports).
   *  - Reports are limited to own_only; settings are view_only.
   *
   * requires_approval — used for actions that affect billing or customer
   * lifecycle, ensuring a senior agent or admin reviews before execution:
   *  - customers:delete — Deleting a customer cascades to subscriptions
   *    and payment history; approval prevents accidental data loss.
   *  - subscriptions:create / subscriptions:pause /
   *    subscriptions:cancel — These change recurring revenue; approval
   *    ensures the action matches the customer's request.
   *  - payment_methods:create / payment_methods:edit — Modifying payment
   *    instruments is sensitive; approval guards against fraud.
   *  - entitlements:create — Granting entitlements outside a subscription
   *    needs oversight to prevent unauthorized access.
   *  - dunning:cancel_attempt — Cancelling a dunning retry can delay
   *    revenue recovery; approval ensures it is intentional.
   * -----------------------------------------------------------------------
   */
  normal_user: {
    'customers:list': 'full',
    'customers:view': 'full',
    'customers:create': 'full',
    'customers:edit': 'own_only',
    'customers:delete': 'requires_approval',
    'customers:export': 'not_allowed',
    'subscriptions:list': 'full',
    'subscriptions:view': 'full',
    'subscriptions:create': 'requires_approval',
    'subscriptions:edit': 'own_only',
    'subscriptions:pause': 'requires_approval',
    'subscriptions:resume': 'full',
    'subscriptions:cancel': 'requires_approval',
    'subscriptions:export': 'not_allowed',
    'invoices:list': 'full',
    'invoices:view': 'full',
    'invoices:create': 'not_allowed',
    'invoices:close': 'not_allowed',
    'invoices:void': 'not_allowed',
    'invoices:write_off': 'not_allowed',
    'invoices:export': 'full',
    'credit_notes:list': 'view_only',
    'credit_notes:view': 'view_only',
    'credit_notes:create': 'not_allowed',
    'transactions:list': 'full',
    'transactions:view': 'full',
    'transactions:export': 'not_allowed',
    'payment_methods:view': 'full',
    'payment_methods:create': 'requires_approval',
    'payment_methods:edit': 'requires_approval',
    'payment_methods:delete': 'not_allowed',
    'entitlements:view': 'full',
    'entitlements:create': 'requires_approval',
    'entitlements:revoke': 'not_allowed',
    'plans:list': 'view_only',
    'plans:view': 'view_only',
    'plans:create': 'not_allowed',
    'plans:edit': 'not_allowed',
    'plans:delete': 'not_allowed',
    'addons:list': 'view_only',
    'addons:view': 'view_only',
    'addons:create': 'not_allowed',
    'addons:edit': 'not_allowed',
    'addons:delete': 'not_allowed',
    'coupons:list': 'view_only',
    'coupons:view': 'view_only',
    'coupons:create': 'not_allowed',
    'coupons:edit': 'not_allowed',
    'coupons:delete': 'not_allowed',
    'dunning:view': 'full',
    'dunning:view_config': 'view_only',
    'dunning:configure': 'not_allowed',
    'dunning:retry': 'full',
    'dunning:cancel_attempt': 'requires_approval',
    'collections:list': 'full',
    'collections:view': 'full',
    'collections:view_config': 'view_only',
    'collections:configure': 'not_allowed',
    'collections:trigger': 'not_allowed',
    'collections:pause': 'not_allowed',
    'collections:resume': 'not_allowed',
    'collections:export': 'not_allowed',
    'alerts:list': 'full',
    'alerts:view': 'full',
    'alerts:manage': 'full',
    'alerts:manage_all': 'not_allowed',
    'developer:api_keys_view': 'not_allowed',
    'developer:api_keys_create': 'not_allowed',
    'developer:api_keys_revoke': 'not_allowed',
    'developer:api_keys_rotate': 'not_allowed',
    'developer:stripe_simulate': 'not_allowed',
    'webhooks:list': 'not_allowed',
    'webhooks:create': 'not_allowed',
    'webhooks:edit': 'not_allowed',
    'webhooks:delete': 'not_allowed',
    'reports:view': 'own_only',
    'reports:export': 'not_allowed',
    'reports:generate': 'not_allowed',
    'settings:view': 'view_only',
    'settings:edit': 'not_allowed',
    'audit_logs:view': 'not_allowed',
    'audit_logs:export': 'not_allowed',
    'users:list': 'not_allowed',
    'users:view': 'own_only',
    'users:create': 'not_allowed',
    'users:edit': 'own_only',
    'users:delete': 'not_allowed',
    'users:assign_role': 'not_allowed',
    'roles:view': 'not_allowed',
    'roles:create': 'not_allowed',
    'roles:edit': 'not_allowed',
    'roles:delete': 'not_allowed',
    // 20. Tenants
    'tenants:list': 'not_allowed',
  },

  /*
   * -----------------------------------------------------------------------
   * FINANCE USER
   * -----------------------------------------------------------------------
   * Access philosophy: Focused on financial operations — invoices, credit
   * notes, transactions, and reports. Has full read access to customers and
   * subscriptions for context, plus full export capabilities on financial
   * data. Can view dunning and collections status but cannot trigger
   * operational actions on them.
   *
   * Restrictions:
   *  - Cannot create, edit, or delete customers (operations team scope).
   *  - Cannot create, edit, pause, resume, or cancel subscriptions.
   *  - Cannot access or modify payment methods (PCI scope boundary).
   *  - Cannot manage developer tools, webhooks, users, or roles.
   *  - Cannot edit system settings or export audit logs.
   *  - Catalog items (plans, addons, coupons) are view_only.
   *
   * requires_approval — used for actions with direct revenue or compliance
   * impact, ensuring a second pair of eyes on financial adjustments:
   *  - invoices:void / invoices:write_off — Irreversible revenue
   *    adjustments that affect financial reporting and reconciliation.
   *  - credit_notes:create — Credit notes reduce accounts receivable;
   *    approval prevents unauthorized write-downs.
   * -----------------------------------------------------------------------
   */
  finance_user: {
    'customers:list': 'full',
    'customers:view': 'full',
    'customers:create': 'not_allowed',
    'customers:edit': 'not_allowed',
    'customers:delete': 'not_allowed',
    'customers:export': 'full',
    'subscriptions:list': 'full',
    'subscriptions:view': 'full',
    'subscriptions:create': 'not_allowed',
    'subscriptions:edit': 'not_allowed',
    'subscriptions:pause': 'not_allowed',
    'subscriptions:resume': 'not_allowed',
    'subscriptions:cancel': 'not_allowed',
    'subscriptions:export': 'full',
    'invoices:list': 'full',
    'invoices:view': 'full',
    'invoices:create': 'full',
    'invoices:close': 'full',
    'invoices:void': 'requires_approval',
    'invoices:write_off': 'requires_approval',
    'invoices:export': 'full',
    'credit_notes:list': 'full',
    'credit_notes:view': 'full',
    'credit_notes:create': 'requires_approval',
    'transactions:list': 'full',
    'transactions:view': 'full',
    'transactions:export': 'full',
    'payment_methods:view': 'not_allowed',
    'payment_methods:create': 'not_allowed',
    'payment_methods:edit': 'not_allowed',
    'payment_methods:delete': 'not_allowed',
    'entitlements:view': 'view_only',
    'entitlements:create': 'not_allowed',
    'entitlements:revoke': 'not_allowed',
    'plans:list': 'view_only',
    'plans:view': 'view_only',
    'plans:create': 'not_allowed',
    'plans:edit': 'not_allowed',
    'plans:delete': 'not_allowed',
    'addons:list': 'view_only',
    'addons:view': 'view_only',
    'addons:create': 'not_allowed',
    'addons:edit': 'not_allowed',
    'addons:delete': 'not_allowed',
    'coupons:list': 'view_only',
    'coupons:view': 'view_only',
    'coupons:create': 'not_allowed',
    'coupons:edit': 'not_allowed',
    'coupons:delete': 'not_allowed',
    'dunning:view': 'full',
    'dunning:view_config': 'view_only',
    'dunning:configure': 'not_allowed',
    'dunning:retry': 'not_allowed',
    'dunning:cancel_attempt': 'not_allowed',
    'collections:list': 'full',
    'collections:view': 'full',
    'collections:view_config': 'view_only',
    'collections:configure': 'not_allowed',
    'collections:trigger': 'not_allowed',
    'collections:pause': 'not_allowed',
    'collections:resume': 'not_allowed',
    'collections:export': 'full',
    'alerts:list': 'full',
    'alerts:view': 'full',
    'alerts:manage': 'full',
    'alerts:manage_all': 'not_allowed',
    'developer:api_keys_view': 'not_allowed',
    'developer:api_keys_create': 'not_allowed',
    'developer:api_keys_revoke': 'not_allowed',
    'developer:api_keys_rotate': 'not_allowed',
    'developer:stripe_simulate': 'not_allowed',
    'webhooks:list': 'not_allowed',
    'webhooks:create': 'not_allowed',
    'webhooks:edit': 'not_allowed',
    'webhooks:delete': 'not_allowed',
    'reports:view': 'full',
    'reports:export': 'full',
    'reports:generate': 'full',
    'settings:view': 'view_only',
    'settings:edit': 'not_allowed',
    'audit_logs:view': 'view_only',
    'audit_logs:export': 'not_allowed',
    'users:list': 'not_allowed',
    'users:view': 'not_allowed',
    'users:create': 'not_allowed',
    'users:edit': 'not_allowed',
    'users:delete': 'not_allowed',
    'users:assign_role': 'not_allowed',
    'roles:view': 'not_allowed',
    'roles:create': 'not_allowed',
    'roles:edit': 'not_allowed',
    'roles:delete': 'not_allowed',
    // 20. Tenants
    'tenants:list': 'not_allowed',
  },

  /*
   * -----------------------------------------------------------------------
   * DEVELOPER
   * -----------------------------------------------------------------------
   * Access philosophy: Technical and integration focus. Full control over
   * API keys, webhooks, Stripe simulation, and system settings (view).
   * Business data (customers, subscriptions, invoices, transactions,
   * catalog items, dunning, collections) is read-only (view_only) to
   * support debugging and integration testing without risk of mutation.
   *
   * Restrictions:
   *  - Cannot create, edit, or delete any business entities (customers,
   *    subscriptions, invoices, credit notes, plans, addons, coupons).
   *  - Cannot access payment methods (PCI scope boundary).
   *  - Cannot view or generate reports, audit logs, or user management.
   *  - Cannot edit system settings (view_only for reference).
   *
   * requires_approval — used for destructive actions on integration
   * infrastructure, ensuring outage-sensitive changes are reviewed:
   *  - developer:api_keys_revoke / developer:api_keys_rotate — Revoking
   *    or rotating an API key can break live integrations; approval
   *    ensures downstream consumers are prepared.
   *  - webhooks:delete — Deleting a webhook endpoint can silently break
   *    event delivery to external systems; approval prevents accidental
   *    removal.
   * -----------------------------------------------------------------------
   */
  developer: {
    'customers:list': 'view_only',
    'customers:view': 'view_only',
    'customers:create': 'not_allowed',
    'customers:edit': 'not_allowed',
    'customers:delete': 'not_allowed',
    'customers:export': 'not_allowed',
    'subscriptions:list': 'view_only',
    'subscriptions:view': 'view_only',
    'subscriptions:create': 'not_allowed',
    'subscriptions:edit': 'not_allowed',
    'subscriptions:pause': 'not_allowed',
    'subscriptions:resume': 'not_allowed',
    'subscriptions:cancel': 'not_allowed',
    'subscriptions:export': 'not_allowed',
    'invoices:list': 'view_only',
    'invoices:view': 'view_only',
    'invoices:create': 'not_allowed',
    'invoices:close': 'not_allowed',
    'invoices:void': 'not_allowed',
    'invoices:write_off': 'not_allowed',
    'invoices:export': 'view_only',
    'credit_notes:list': 'view_only',
    'credit_notes:view': 'view_only',
    'credit_notes:create': 'not_allowed',
    'transactions:list': 'view_only',
    'transactions:view': 'view_only',
    'transactions:export': 'not_allowed',
    'payment_methods:view': 'not_allowed',
    'payment_methods:create': 'not_allowed',
    'payment_methods:edit': 'not_allowed',
    'payment_methods:delete': 'not_allowed',
    'entitlements:view': 'view_only',
    'entitlements:create': 'not_allowed',
    'entitlements:revoke': 'not_allowed',
    'plans:list': 'view_only',
    'plans:view': 'view_only',
    'plans:create': 'not_allowed',
    'plans:edit': 'not_allowed',
    'plans:delete': 'not_allowed',
    'addons:list': 'view_only',
    'addons:view': 'view_only',
    'addons:create': 'not_allowed',
    'addons:edit': 'not_allowed',
    'addons:delete': 'not_allowed',
    'coupons:list': 'view_only',
    'coupons:view': 'view_only',
    'coupons:create': 'not_allowed',
    'coupons:edit': 'not_allowed',
    'coupons:delete': 'not_allowed',
    'dunning:view': 'view_only',
    'dunning:view_config': 'view_only',
    'dunning:configure': 'not_allowed',
    'dunning:retry': 'not_allowed',
    'dunning:cancel_attempt': 'not_allowed',
    'collections:list': 'view_only',
    'collections:view': 'view_only',
    'collections:view_config': 'view_only',
    'collections:configure': 'not_allowed',
    'collections:trigger': 'not_allowed',
    'collections:pause': 'not_allowed',
    'collections:resume': 'not_allowed',
    'collections:export': 'not_allowed',
    'alerts:list': 'full',
    'alerts:view': 'full',
    'alerts:manage': 'full',
    'alerts:manage_all': 'not_allowed',
    'developer:api_keys_view': 'full',
    'developer:api_keys_create': 'full',
    'developer:api_keys_revoke': 'requires_approval',
    'developer:api_keys_rotate': 'requires_approval',
    'developer:stripe_simulate': 'full',
    'webhooks:list': 'full',
    'webhooks:create': 'full',
    'webhooks:edit': 'full',
    'webhooks:delete': 'requires_approval',
    'reports:view': 'not_allowed',
    'reports:export': 'not_allowed',
    'reports:generate': 'not_allowed',
    'settings:view': 'full',
    'settings:edit': 'not_allowed',
    'audit_logs:view': 'not_allowed',
    'audit_logs:export': 'not_allowed',
    'users:list': 'not_allowed',
    'users:view': 'not_allowed',
    'users:create': 'not_allowed',
    'users:edit': 'not_allowed',
    'users:delete': 'not_allowed',
    'users:assign_role': 'not_allowed',
    'roles:view': 'not_allowed',
    'roles:create': 'not_allowed',
    'roles:edit': 'not_allowed',
    'roles:delete': 'not_allowed',
    // 20. Tenants
    'tenants:list': 'not_allowed',
  },
};
